import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider
} from '@mui/material';
import { Add as AddIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import axiosInstance from '../../API/axios';

const RequestApproverDialog = ({ open, onClose, exitRequestId, onSaved }) => {
    const [formData, setFormData] = useState({
        approverId: null,
        approverUsername: '',
        approverEmail: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [loadingApprovers, setLoadingApprovers] = useState(false);
    const [isNewApprover, setIsNewApprover] = useState(false);
    const [emailCheckLoading, setEmailCheckLoading] = useState(false);
    const [emailExists, setEmailExists] = useState(null);
    const [existingUserInfo, setExistingUserInfo] = useState(null);

    // Fetch existing approver users when dialog opens
    useEffect(() => {
        if (open) {
            fetchApprovers();
            // Reset form
            setFormData({ approverId: null, approverUsername: '', approverEmail: '' });
            setIsNewApprover(false);
            setError(null);
            setEmailExists(null);
            setExistingUserInfo(null);
        }
    }, [open]);

    const fetchApprovers = async () => {
        setLoadingApprovers(true);
        try {
            const response = await axiosInstance.get('/users/role?roleName=APPROVER');
            setApprovers(response.data || []);
        } catch (err) {
            console.error('Error fetching approvers:', err);
            setApprovers([]);
        } finally {
            setLoadingApprovers(false);
        }
    };

    // Check if email exists when typing new email
    useEffect(() => {
        if (!isNewApprover || !formData.approverEmail) {
            setEmailExists(null);
            setExistingUserInfo(null);
            return;
        }

        const checkEmail = async () => {
            if (formData.approverEmail.includes('@')) {
                setEmailCheckLoading(true);
                try {
                    const response = await axiosInstance.get(`/users/check-email?email=${encodeURIComponent(formData.approverEmail)}`);
                    setEmailExists(response.data.exists);
                    if (response.data.exists) {
                        setExistingUserInfo({
                            username: response.data.username,
                            role: response.data.role,
                            isApprover: response.data.role === 'APPROVER'
                        });
                        if (response.data.role === 'APPROVER') {
                            setFormData(prev => ({
                                ...prev,
                                approverUsername: response.data.username || prev.approverUsername
                            }));
                        }
                    } else {
                        setExistingUserInfo(null);
                    }
                } catch {
                    setEmailExists(false);
                    setExistingUserInfo(null);
                } finally {
                    setEmailCheckLoading(false);
                }
            }
        };

        const debounce = setTimeout(checkEmail, 500);
        return () => clearTimeout(debounce);
    }, [formData.approverEmail, isNewApprover]);

    const handleApproverSelect = (event) => {
        const selectedId = event.target.value;
        if (selectedId === 'new') {
            setIsNewApprover(true);
            setFormData({ approverId: null, approverUsername: '', approverEmail: '' });
        } else if (selectedId) {
            const approver = approvers.find(a => a.id === selectedId);
            if (approver) {
                setIsNewApprover(false);
                setFormData({
                    approverId: approver.id,
                    approverUsername: approver.username || '',
                    approverEmail: approver.email || ''
                });
            }
        } else {
            setIsNewApprover(false);
            setFormData({ approverId: null, approverUsername: '', approverEmail: '' });
        }
    };

    const handleChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    const validateForm = () => {
        if (!formData.approverEmail.trim()) {
            setError('Approver email is required');
            return false;
        }
        if (!formData.approverEmail.includes('@')) {
            setError('Please enter a valid email address');
            return false;
        }
        if (isNewApprover && !formData.approverUsername.trim()) {
            setError('Approver username is required');
            return false;
        }
        if (isNewApprover && emailExists && existingUserInfo && !existingUserInfo.isApprover) {
            setError(`This email belongs to an existing ${existingUserInfo.role || 'user'} account. Please select an Approver from the dropdown or enter a different email.`);
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setError(null);
        try {
            await axiosInstance.post(`/exitRequest/${exitRequestId}/request-approver`, {
                approverUsername: formData.approverUsername.trim() || existingUserInfo?.username || '',
                approverEmail: formData.approverEmail.trim()
            });
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error('Error requesting approver:', err);
            setError(err.response?.data?.message || 'Failed to add approver');
        } finally {
            setLoading(false);
        }
    };

    const showNewApproverWarning = isNewApprover && emailExists === false && formData.approverEmail.includes('@');
    const showExistingNonApproverError = isNewApprover && emailExists && existingUserInfo && !existingUserInfo.isApprover;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <PersonAddIcon />
                    <Typography variant="h6">Add Approver</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Approver Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Select an existing approver or create a new one</InputLabel>
                            <Select
                                value={isNewApprover ? 'new' : (formData.approverId || '')}
                                onChange={handleApproverSelect}
                                label="Select an existing approver or create a new one"
                                disabled={loadingApprovers}
                            >
                                {approvers.map((approver) => (
                                    <MenuItem key={approver.id} value={approver.id}>
                                        {approver.username} ({approver.email})
                                    </MenuItem>
                                ))}
                                <Divider />
                                <MenuItem value="new">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PersonAddIcon fontSize="small" color="primary" />
                                        <Typography color="primary">Create New Approver Account</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                        {loadingApprovers && (
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="textSecondary">Loading approvers...</Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* New approver warning */}
                    {showNewApproverWarning && (
                        <Grid item xs={12}>
                            <Alert severity="warning" icon={<PersonAddIcon />}>
                                <strong>New Approver Account Will Be Created</strong>
                                <br />
                                No user found with this email. A new Approver account will be created automatically. The user will receive login instructions via email.
                            </Alert>
                        </Grid>
                    )}

                    {/* Error: email belongs to different role */}
                    {showExistingNonApproverError && (
                        <Grid item xs={12}>
                            <Alert severity="error">
                                <strong>Cannot Use This Email</strong>
                                <br />
                                This email belongs to an existing {existingUserInfo?.role} account. Please select an existing Approver from the dropdown or enter a different email.
                            </Alert>
                        </Grid>
                    )}

                    {/* Fields - shown only when creating a new approver or selecting existing */}
                    {isNewApprover && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Username *"
                                    value={formData.approverUsername}
                                    onChange={handleChange('approverUsername')}
                                    fullWidth
                                    placeholder="Enter username"
                                    disabled={emailExists && existingUserInfo?.isApprover}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Email *"
                                    type="email"
                                    value={formData.approverEmail}
                                    onChange={handleChange('approverEmail')}
                                    fullWidth
                                    placeholder="email@unilever.com"
                                    helperText={emailCheckLoading ? 'Checking email...' : ''}
                                    InputProps={{
                                        endAdornment: emailCheckLoading ? <CircularProgress size={20} /> : null
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || (!formData.approverId && !isNewApprover) || showExistingNonApproverError}
                    startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                >
                    {loading ? 'Adding...' : 'Add Approver'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RequestApproverDialog;
